import * as turf from '@turf/turf';
import { getInfoFromMapBox } from 'sonata-static-storage/lib/entities';
import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';

import type { MapStyle } from './map.types';

type MapboxGeocoderFeature = {
  place_name?: string;
  place_type: string[];
  properties?: {
    short_code?: string;
  };
};

type MapboxGeocoderResponse = {
  features?: MapboxGeocoderFeature[];
};

export type LocationInfo = {
  country: string;
  placeName: string;
};

export const fitBoundsOptions = {
  duration: 1000,
  linear: false,
  padding: 20
};

export const getMapStyle = (styleSelected: MapStyle): string => {
  switch (styleSelected) {
    case 'dark':
      return 'mapbox://styles/taptap/cl78tjcu2000p14rptx9um21b';
    case 'light':
      return 'mapbox://styles/taptap/cl78y77dw000e15p2gw08kt1k';
    case 'streets':
      return 'mapbox://styles/taptap/cl78vetip001215mgd7w7yyuu';
    case 'satellite':
      return 'mapbox://styles/taptap/cl78wx651000d15qul16yyqi7';
    case 'default':
    default:
      return 'mapbox://styles/taptap/cl78y77dw000e15p2gw08kt1k';
  }
};

export const sanitizeObj = (obj: Record<string, any>) =>
  Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (key === 'geojson') {
        acc[key] = value === null || value === undefined ? {} : value;
      } else {
        acc[key] = value === null ? '' : value;
      }
      return acc;
    },
    {} as Record<string, any>
  );

const getBBoxCoordinates = (polygon: GeoJSON.Feature<GeoJSON.Polygon>) => {
  const { geometry } = polygon;
  return turf.bbox(geometry);
};

export const centerMap = (zone: any, map: any) => {
  if (zone.type === 'polygon' && zone.geojson?.geometry) {
    const bbox = getBBoxCoordinates(zone.geojson);
    map?.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]]
      ],
      fitBoundsOptions
    );
  } else if (['circle', 'torus'].includes(zone.type) && zone.latlng?.lat && zone.latlng?.lng && zone.radius) {
    const bounds = turf.circle([zone.latlng.lng, zone.latlng.lat], zone.radius / 1000, { units: 'kilometers' });
    const bbox = turf.bbox(bounds);
    map?.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]]
      ],
      fitBoundsOptions
    );
  } else if (zone.type === 'geojson' && zone.boundingBox) {
    const bbox = zone.boundingBox;
    map?.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]]
      ],
      fitBoundsOptions
    );
  }
};

export const findCountryFeature = (feature: Record<string, any>) => feature.place_type.includes('country');

export const findAddressFeature = (feature: Record<string, any>) => feature.place_type.includes('address');

/**
 * Resolves the cca3 country code at the given coordinates using Mapbox geocoding.
 * Returns an empty string when the country cannot be determined or the request fails.
 */
export const resolveCountryCca3 = async (lat: number, lng: number): Promise<string> => {
  try {
    const info = (await getInfoFromMapBox(lat, lng)) as MapboxGeocoderResponse | null;
    const countryFeature = info?.features?.find(findCountryFeature);
    return normalizeCountryToCca3(countryFeature?.properties?.short_code ?? '') || '';
  } catch {
    return '';
  }
};

export const resolveLocationInfo = async (lat: number, lng: number): Promise<LocationInfo> => {
  try {
    const info = (await getInfoFromMapBox(lat, lng)) as MapboxGeocoderResponse | null;
    const features = info?.features ?? [];
    const countryFeature = features.find(findCountryFeature);
    const addressFeature = features.find(findAddressFeature) as MapboxGeocoderFeature | undefined;
    const country = normalizeCountryToCca3(countryFeature?.properties?.short_code ?? '') || '';
    const placeName = addressFeature?.place_name ?? '';
    return { country, placeName };
  } catch {
    return { country: '', placeName: '' };
  }
};
